<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Produtos</h5>
      <b-button
        v-if="hasPermission('adicionar_produtos')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-box-seam" title="Novo Produto"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por Nome"
        class="form-control"
        @input="filtrar"
      />
      <input
        v-model="filters.codigo"
        placeholder="Filtrar por Código"
        class="form-control"
        @input="filtrar"
      />
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center"
      >
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th><button class="btn btn-link p-0" @click="sort('codigo')">Código {{ arrow('codigo') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('nome')">Nome {{ arrow('nome') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('estoque')">Estoque {{ arrow('estoque') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('preco_venda')">Preço Venda {{ arrow('preco_venda') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('ativo')">Ativo {{ arrow('ativo') }}</button></th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="prod in produtos" :key="prod.id">
          <td>{{ prod.codigo }}</td>
          <td>{{ prod.nome }}</td>
          <td>{{ prod.estoque }}</td>
          <td>{{ formatCurrency(prod.preco_venda) }}</td>
          <td>{{ prod.ativo ? 'Sim' : 'Não' }}</td>
          <td class="text-end">
            <i v-if="hasPermission('detalhar_produtos')" class="bi bi-eye-fill text-primary mx-2" @click="detalhar(prod.id)"></i>
            <i v-if="hasPermission('alterar_produtos')" class="bi bi-pencil-fill text-warning mx-2" @click="editar(prod.id)"></i>
            <i v-if="hasPermission('excluir_produtos')" class="bi bi-trash-fill text-danger mx-2" @click="excluir(prod.id)"></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- 1. Resumo -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <!-- 2. Seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limitePorPagina"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="onChangeLimit"
        />
      </div>

      <!-- 3. Controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, getCurrentInstance, computed, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'

const router = useRouter()
const { proxy } = getCurrentInstance()

// composable para produtos
const {
  items: produtos,
  totalItems,
  page: paginaAtual,
  limit: limitePorPagina,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/produtos`,
  { key: 'nome', order: 'asc' }
)

// opções de limite de páginas
const pageOptions = ref([10, 15, 20, 25, 50])

// Computeds para resumo
const startRow = computed(() =>
  (paginaAtual.value - 1) * limitePorPagina.value + 1
)
const endRow = computed(() =>
  Math.min(paginaAtual.value * limitePorPagina.value, totalItems.value)
)

// Watchers para recarregar
watch(paginaAtual, () => load())
watch(limitePorPagina, () => {
  paginaAtual.value = 1
  load()
})

// Inicialização
onMounted(() => {
  filters.value = {
    nome: '',
    codigo: '',
    categoria_id: undefined,
    ativo: undefined
  }
  load()
})

// Filtros
function filtrar() {
  paginaAtual.value = 1
  load()
}
function limparFiltros() {
  filters.value = {
    nome: '',
    codigo: '',
    categoria_id: undefined,
    ativo: undefined
  }
  paginaAtual.value = 1
  load()
}

// Handlers de paginação
function onChangeLimit() {
  paginaAtual.value = 1
  load()
}
function onPageChange() {
  load()
}

// Navegação e ações
function irParaCadastro() { router.push('/cadastros/produtos/novo') }
function detalhar(id)       { router.push(`/cadastros/produtos/${id}/detalhar`) }
function editar(id)         { router.push(`/cadastros/produtos/${id}`) }
async function excluir(id) {
  const ok = await proxy.$confirm('Confirma exclusão do produto?','Confirmação')
  if (!ok) return
  await axios.delete(`/cadastros/produtos/${id}`)
  load()
  proxy.$toast.success('Produto excluído')
}

function formatCurrency(val) {
  return val != null
    ? Number(val).toLocaleString('pt-BR',{ style:'currency', currency:'BRL' })
    : '—'
}
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
</style>